import { computed, unref, type Ref, type ComputedRef } from 'vue'

/**
 * Universal type for reactive or static values
 * 
 * USAGE GUIDELINES:
 * - Use in composables and API query functions
 * - For Vue components, prefer standard Vue 3 computed(() => props.value) approach  
 * - Helps create flexible APIs that accept both reactive and static values
 */
export type MaybeReactive<T> = T | Ref<T> | ComputedRef<T>

/**
 * Universal utility to extract value from reactive or static data
 * Handles Ref, ComputedRef, or plain values
 * 
 * RECOMMENDED FOR:
 * - Composables that need to work with mixed reactive/static inputs
 * - API query parameter processing
 * - Utility functions that transform data
 */
export function extractReactiveValue<T>(value: MaybeReactive<T>): T {
  return unref(value)
}

/**
 * Create a computed that safely extracts value from potentially reactive parameter
 * Eliminates the need for manual type checking everywhere
 * 
 * RECOMMENDED FOR:
 * - Composables expecting MaybeReactive parameters  
 * - Converting non-reactive values to reactive ones
 * - NOT recommended for simple Vue component props (use computed(() => props.value) instead)
 */
export function createReactiveComputed<T>(value: MaybeReactive<T>) {
  return computed(() => extractReactiveValue(value))
}



/**
 * Create computed from reactive parameters for API queries
 * Preserves original types while handling reactivity
 * 
 * RECOMMENDED FOR:
 * - API query parameter objects
 * - Composables that need to transform parameter objects
 */
export function createQueryParams<T extends Record<string, any>>(
  params: { [K in keyof T]: MaybeReactive<T[K]> }
): ComputedRef<T> {
  return computed(() => {
    const result = {} as T
    for (const key in params) {
      const extractedValue = extractReactiveValue(params[key])
      if (extractedValue !== undefined && extractedValue !== null) {
        result[key] = extractedValue as T[Extract<keyof T, string>]
      }
    }
    return result
  })
} 